import ExcelJS from 'exceljs';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import FONT_ROBOTO_REGULAR from './pdf-font';

/**
 * EG-344 — Client-side export report theo CSV / Excel / PDF.
 *
 * Sử dụng:
 *   exportReport({
 *     filename: 'bao-cao-doanh-thu',
 *     format: 'excel',
 *     sheets: [
 *       { name: 'Tổng quan', columns: [...], rows: [...] },
 *       { name: 'Theo ngày',  columns: [...], rows: [...] },
 *     ],
 *   });
 */

export type ExportFormat = 'csv' | 'excel' | 'pdf';

export interface ExcelStyleOptions {
  /** Màu nền header, HEX hoặc ARGB (mặc định: '8B0000' cho Ancarat) */
  headerBgColor?: string;
  /** Màu chữ header, HEX hoặc ARGB (mặc định: 'FFFFFF') */
  headerTextColor?: string;
  /** Bật zebra striping xen kẽ hàng chẵn/lẻ (mặc định: true) */
  zebraStriping?: boolean;
  /** Màu nền dòng chẵn khi bật zebra (mặc định: 'F9FAFB') */
  zebraColor?: string;
  /** Màu đường viền cell (mặc định: 'D3D3D3') */
  borderColor?: string;
}

export interface ExportColumn {
  /** Header text hiển thị trong file. */
  header: string;
  /** Key trong row object để lấy value. Ngoài ra có thể tự render qua `format`. */
  key: string;
  /** Optional formatter trước khi xuất (vd: VND, %). */
  format?: (value: unknown, row: Record<string, unknown>) => string | number;
}

export interface SummaryCardItem {
  label: string;
  value: string | number;
}

export interface ExportSheet {
  /** Tên sheet (Excel) hoặc title (PDF page). CSV: ignored. */
  name: string;
  columns: ExportColumn[];
  rows: Array<Record<string, unknown>>;
  /** Tiêu đề báo cáo hiển thị ở đầu sheet. */
  title?: string;
  /** Danh sách điều kiện lọc đang áp dụng (vd: { 'Thời gian lọc': '01/06/2026 - 30/06/2026' }). */
  filterInfo?: Record<string, string>;
  /** Danh sách thẻ KPI / thông số tổng quan. */
  summaryCards?: SummaryCardItem[];
  /** Tự động hiển thị dòng TỔNG CỘNG ở cuối bảng. */
  showTotalRow?: boolean;
}

export interface ExportInput {
  /** Tên file (không kèm extension). */
  filename: string;
  format: ExportFormat;
  sheets: ExportSheet[];
  /** Title hiển thị đầu PDF / metadata. Default = filename. */
  title?: string;
  /** Tùy chọn định dạng style cho Excel. */
  excelStyle?: ExcelStyleOptions;
}

export function toARGB(colorStr?: string, defaultColor: string = 'FFFFFF'): string {
  const clean = (colorStr || defaultColor).replace('#', '').trim();
  if (clean.length === 6) return `FF${clean.toUpperCase()}`;
  if (clean.length === 8) return clean.toUpperCase();
  return `FF${defaultColor.replace('#', '').trim().toUpperCase()}`;
}

function toAOA(sheet: ExportSheet, titleOverride?: string): Array<Array<string | number>> {
  const result: Array<Array<string | number>> = [];

  const title = sheet.title || titleOverride;
  if (title) {
    result.push([title]);
  }

  if (sheet.filterInfo && Object.keys(sheet.filterInfo).length > 0) {
    const filterLine = Object.entries(sheet.filterInfo)
      .map(([k, v]) => `${k}: ${v}`)
      .join(' | ');
    result.push([filterLine]);
  }

  if (sheet.summaryCards && sheet.summaryCards.length > 0) {
    const summaryLine = sheet.summaryCards.map((c) => `${c.label}: ${c.value}`).join(' | ');
    result.push([summaryLine]);
    result.push([]); // blank line separator
  } else if (title || sheet.filterInfo) {
    result.push([]); // blank line separator
  }

  const header = sheet.columns.map((c) => c.header);
  result.push(header);

  const body = sheet.rows.map((row) =>
    sheet.columns.map((col) => {
      const raw = row[col.key];
      if (col.format) {
        const out = col.format(raw, row);
        return out === undefined || out === null ? '' : out;
      }
      if (raw === undefined || raw === null) return '';
      if (typeof raw === 'number' || typeof raw === 'string') return raw;
      return String(raw);
    }),
  );
  result.push(...body);

  if (sheet.showTotalRow && sheet.rows.length > 0) {
    const totalRow = sheet.columns.map((col, idx) => {
      if (idx === 0) return 'TỔNG CỘNG';
      const isNumericCol = sheet.rows.every((r) => {
        const val = r[col.key];
        return val === undefined || val === null || typeof val === 'number' || !isNaN(Number(val));
      });
      const hasNumbers = sheet.rows.some((r) => typeof r[col.key] === 'number');
      if (isNumericCol && hasNumbers) {
        const sum = sheet.rows.reduce((acc, r) => acc + Number(r[col.key] ?? 0), 0);
        if (col.format) {
          const formatted = col.format(sum, {});
          return formatted === undefined || formatted === null ? sum : formatted;
        }
        return sum;
      }
      return '';
    });
    result.push(totalRow);
  }

  return result;
}

function downloadBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

function exportCsv(input: ExportInput) {
  // CSV chỉ ghi sheet đầu (CSV là single-sheet format).
  const sheet = input.sheets[0];
  if (!sheet) throw new Error('exportReport: cần ít nhất 1 sheet');
  const aoa = toAOA(sheet, input.title);
  const csv = aoa
    .map((row) =>
      row
        .map((cell) => {
          const s = String(cell ?? '');
          // Escape: nếu có dấu phẩy / ngoặc kép / xuống dòng → quote + double-quote.
          if (/[",\n\r]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
          return s;
        })
        .join(','),
    )
    .join('\r\n');
  // BOM ﻿ để Excel mở UTF-8 chuẩn (tiếng Việt không bị mojibake).
  const blob = new Blob(['﻿', csv], { type: 'text/csv;charset=utf-8' });
  downloadBlob(blob, `${input.filename}.csv`);
}

/**
 * Tạo buffer .xlsx đã style (bold header + freeze + border vùng dữ liệu + Ancarat theme & zebra striping).
 * Hàm thuần — không đụng DOM, dùng cho cả download lẫn unit test.
 */
export async function buildExcelBuffer(input: ExportInput): Promise<ArrayBuffer> {
  const wb = new ExcelJS.Workbook();

  const excelStyle = input.excelStyle ?? {};
  const headerBgColorARGB = toARGB(excelStyle.headerBgColor, '8B0000');
  const headerTextColorARGB = toARGB(excelStyle.headerTextColor, 'FFFFFF');
  const zebraStriping = excelStyle.zebraStriping ?? true;
  const zebraColorARGB = toARGB(excelStyle.zebraColor, 'F9FAFB');
  const borderColorARGB = toARGB(excelStyle.borderColor, 'D3D3D3');

  const thinBorder: ExcelJS.Border = { style: 'thin', color: { argb: borderColorARGB } };
  const cellBorder: Partial<ExcelJS.Borders> = {
    top: thinBorder,
    left: thinBorder,
    bottom: thinBorder,
    right: thinBorder,
  };

  for (const sheet of input.sheets) {
    const ws = wb.addWorksheet(sheet.name.slice(0, 31));
    const colCount = sheet.columns.length;
    let rIdx = 1;

    const sheetTitle = sheet.title || input.title;
    if (sheetTitle) {
      const titleRow = ws.getRow(rIdx);
      titleRow.height = 30;
      const titleCell = titleRow.getCell(1);
      titleCell.value = sheetTitle;
      titleCell.font = { bold: true, size: 14, color: { argb: headerBgColorARGB } };
      titleCell.alignment = { vertical: 'middle', horizontal: 'left' };
      rIdx++;
    }

    if (sheet.filterInfo && Object.keys(sheet.filterInfo).length > 0) {
      const filterLine = Object.entries(sheet.filterInfo)
        .map(([k, v]) => `${k}: ${v}`)
        .join('  |  ');
      const filterRow = ws.getRow(rIdx);
      filterRow.height = 20;
      const filterCell = filterRow.getCell(1);
      filterCell.value = filterLine;
      filterCell.font = { italic: true, size: 10, color: { argb: 'FF555555' } };
      filterCell.alignment = { vertical: 'middle', horizontal: 'left' };
      rIdx++;
    }

    if (sheet.summaryCards && sheet.summaryCards.length > 0) {
      const labelRow = ws.getRow(rIdx);
      const valRow = ws.getRow(rIdx + 1);
      labelRow.height = 18;
      valRow.height = 22;

      sheet.summaryCards.forEach((card, idx) => {
        const c = idx + 1;
        if (c <= colCount) {
          const lCell = labelRow.getCell(c);
          lCell.value = card.label;
          lCell.font = { bold: true, size: 9, color: { argb: 'FF4B5563' } };
          lCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF3F4F6' } };
          lCell.alignment = { vertical: 'middle', horizontal: 'center' };
          lCell.border = cellBorder;

          const vCell = valRow.getCell(c);
          vCell.value = card.value;
          vCell.font = { bold: true, size: 11, color: { argb: 'FF111827' } };
          vCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF3F4F6' } };
          vCell.alignment = { vertical: 'middle', horizontal: 'center' };
          vCell.border = cellBorder;
        }
      });
      rIdx += 2;
    }

    if (sheetTitle || sheet.filterInfo || sheet.summaryCards) {
      rIdx++; // Blank line spacing
    }

    const headerRowIndex = rIdx;
    const headerRow = ws.getRow(headerRowIndex);
    headerRow.height = 26;

    sheet.columns.forEach((col, idx) => {
      const cell = headerRow.getCell(idx + 1);
      cell.value = col.header;
      cell.border = cellBorder;
      cell.font = { bold: true, color: { argb: headerTextColorARGB }, size: 11 };
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: headerBgColorARGB } };
      cell.alignment = { vertical: 'middle', horizontal: 'center', wrapText: true };
    });

    ws.views = [{ state: 'frozen', ySplit: headerRowIndex }];

    rIdx++;

    const dataStartRow = rIdx;
    sheet.rows.forEach((rawRow) => {
      const row = ws.getRow(rIdx);
      row.height = 20;
      const isEvenRow = rIdx % 2 === 0;

      sheet.columns.forEach((col, cIdx) => {
        const cell = row.getCell(cIdx + 1);
        cell.border = cellBorder;

        if (zebraStriping && isEvenRow) {
          cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: zebraColorARGB } };
        }

        const rawVal = rawRow ? rawRow[col.key] : undefined;
        const headerLower = col.header.toLowerCase();
        const keyLower = col.key.toLowerCase();

        const isCentered =
          keyLower === 'stt' ||
          headerLower === 'stt' ||
          keyLower.includes('code') ||
          keyLower.includes('number') ||
          headerLower.includes('mã') ||
          keyLower.includes('state') ||
          keyLower.includes('status') ||
          headerLower.includes('trạng thái') ||
          col.format === formatDateCell ||
          keyLower.includes('date') ||
          keyLower.includes('time') ||
          keyLower.endsWith('_at') ||
          headerLower.includes('ngày') ||
          headerLower.includes('thời gian');

        const isNumber =
          !isCentered &&
          (col.format === formatNumberCell ||
            typeof cell.value === 'number' ||
            typeof rawVal === 'number');

        if (isNumber) {
          if (rawVal != null && !isNaN(Number(rawVal))) {
            cell.value = Number(rawVal);
          }
          cell.numFmt = '#,##0';
          cell.alignment = { vertical: 'middle', horizontal: 'right' };
        } else if (col.format) {
          const formatted = col.format(rawVal, rawRow);
          cell.value = formatted === undefined || formatted === null ? '' : (formatted as ExcelJS.CellValue);
          if (isCentered) {
            cell.alignment = { vertical: 'middle', horizontal: 'center' };
          } else {
            cell.alignment = { vertical: 'middle', horizontal: 'left' };
          }
        } else {
          cell.value = rawVal === undefined || rawVal === null ? '' : (rawVal as ExcelJS.CellValue);
          if (isCentered) {
            cell.alignment = { vertical: 'middle', horizontal: 'center' };
          } else {
            cell.alignment = { vertical: 'middle', horizontal: 'left' };
          }
        }
      });
      rIdx++;
    });

    if (sheet.showTotalRow && sheet.rows.length > 0) {
      const totalRow = ws.getRow(rIdx);
      totalRow.height = 24;
      const doubleBottomBorder: Partial<ExcelJS.Borders> = {
        top: thinBorder,
        left: thinBorder,
        bottom: { style: 'double', color: { argb: borderColorARGB } },
        right: thinBorder,
      };

      sheet.columns.forEach((col, cIdx) => {
        const cell = totalRow.getCell(cIdx + 1);
        cell.border = doubleBottomBorder;
        cell.font = { bold: true, size: 11 };
        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFE5E7EB' } };

        if (cIdx === 0) {
          cell.value = 'TỔNG CỘNG';
          cell.alignment = { vertical: 'middle', horizontal: 'center' };
        } else {
          const isNumericCol = sheet.rows.every((r) => {
            const val = r[col.key];
            return val === undefined || val === null || typeof val === 'number' || !isNaN(Number(val));
          });
          const hasNumbers = sheet.rows.some((r) => typeof r[col.key] === 'number');

          if (isNumericCol && hasNumbers) {
            const sum = sheet.rows.reduce((acc, r) => acc + Number(r[col.key] ?? 0), 0);
            cell.value = sum;
            cell.numFmt = '#,##0';
            cell.alignment = { vertical: 'middle', horizontal: 'right' };
          }
        }
      });
      rIdx++;
    }

    // Auto-size column width dynamically
    sheet.columns.forEach((col, idx) => {
      let maxLen = col.header.length;
      for (let r = dataStartRow; r < rIdx; r++) {
        const cellVal = String(ws.getRow(r).getCell(idx + 1).value ?? '');
        if (cellVal.length > maxLen) maxLen = cellVal.length;
      }
      ws.getColumn(idx + 1).width = Math.min(Math.max(maxLen + 4, 12), 60);
    });
  }
  const buffer = await wb.xlsx.writeBuffer();
  return buffer as ArrayBuffer;
}

async function exportExcel(input: ExportInput): Promise<void> {
  const buffer = await buildExcelBuffer(input);
  const blob = new Blob([buffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
  downloadBlob(blob, `${input.filename}.xlsx`);
}

function exportPdf(input: ExportInput) {
  const doc = new jsPDF({ orientation: 'landscape', unit: 'pt', format: 'a4' });
  // Register custom Roboto font for Vietnamese diacritics support
  const fontData = atob(FONT_ROBOTO_REGULAR);
  doc.addFileToVFS('Roboto-Regular.ttf', fontData);
  doc.addFont('Roboto-Regular.ttf', 'Roboto', 'normal', 'Identity-H');
  // autoTable renders header cells with fontStyle 'bold'; register the bold
  // style too (same Roboto TTF) so headers don't fall back to Helvetica-Bold,
  // which lacks Vietnamese diacritics and garbles column headers.
  doc.addFont('Roboto-Regular.ttf', 'Roboto', 'bold', 'Identity-H');
  doc.setFont('Roboto', 'normal');

  const title = input.title ?? input.filename;
  doc.setFontSize(14);
  doc.text(title, 40, 32);
  doc.setFontSize(9);
  doc.text(
    `Xuất lúc: ${new Date().toLocaleString('vi-VN')}`,
    40,
    48,
  );

  let cursorY = 64;
  for (const sheet of input.sheets) {
    if (cursorY > 500) {
      doc.addPage();
      cursorY = 40;
    }
    doc.setFontSize(11);
    doc.text(sheet.name, 40, cursorY);
    cursorY += 8;
    autoTable(doc, {
      startY: cursorY,
      head: [sheet.columns.map((c) => c.header)],
      body: sheet.rows.map((row) =>
        sheet.columns.map((col) => {
          const raw = row[col.key];
          if (col.format) {
            const v = col.format(raw, row);
            return v === undefined || v === null ? '' : String(v);
          }
          if (raw === undefined || raw === null) return '';
          return String(raw);
        }),
      ),
      styles: { fontSize: 9, font: 'Roboto' },
      headStyles: { fillColor: [164, 25, 61] },
      margin: { left: 40, right: 40 },
    });
    // jsPDF AutoTable — last Y position
    cursorY = (doc as unknown as { lastAutoTable?: { finalY: number } }).lastAutoTable?.finalY ?? cursorY;
    cursorY += 24;
  }

  doc.save(`${input.filename}.pdf`);
}

export async function exportReport(input: ExportInput): Promise<void> {
  switch (input.format) {
    case 'csv':
      return exportCsv(input);
    case 'excel':
      return exportExcel(input);
    case 'pdf':
      return exportPdf(input);
  }
}

/**
 * Helper format VND cho cột number (export không decimal).
 */
export const formatNumberCell = (v: unknown) => {
  const n = typeof v === 'number' ? v : Number(v);
  if (Number.isNaN(n)) return '';
  return n.toLocaleString('vi-VN');
};

/**
 * Múi giờ nghiệp vụ. Cố định chứ không lấy múi giờ máy người dùng: bộ lọc ngày ở BE
 * (`parseDateFilter` trong `orders-aggregate.service.ts`) dựng biên ngày bằng
 * `+07:00` cứng, nên file xuất phải cắt ngày theo đúng múi giờ đó thì "lọc ngày 25/8"
 * và "cột ngày ghi 25/8" mới là cùng một ngày.
 */
const EXPORT_TZ = 'Asia/Ho_Chi_Minh';

const EXPORT_DATE_FORMATTER = new Intl.DateTimeFormat('en-GB', {
  timeZone: EXPORT_TZ,
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
});

/**
 * Helper format ngày → DD/MM/YYYY.
 *
 * TCR-1491 / TCR-1492: bản cũ cắt chuỗi bằng regex `^(\d{4})-(\d{2})-(\d{2})`, tức lấy
 * phần ngày của UTC. Đơn tạo 06:40 ngày 25/08 giờ VN được lưu `2026-08-24T23:40:54Z`
 * nên file ghi 24/08 trong khi màn hình ghi 25/08 (TCR-1492) — và mọi đơn tạo trước
 * 07:00 giờ VN đều bị lùi 1 ngày, khiến tester đọc file tưởng thiếu đơn (TCR-1491).
 *
 * Chuỗi ngày THUẦN (`YYYY-MM-DD`, không có phần giờ) vẫn giữ nguyên cách cũ: nó không
 * mang thời điểm nào cả, quy đổi múi giờ chỉ tự đẻ ra lệch ngày mới.
 */
export const formatDateCell = (v: unknown) => {
  if (typeof v !== 'string') return '';
  const m = v.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return v;
  // Ngày thuần — không có phần giờ để mà quy đổi.
  if (/^\d{4}-\d{2}-\d{2}$/.test(v)) return `${m[3]}/${m[2]}/${m[1]}`;

  const parsed = new Date(v);
  // Chuỗi bắt đầu giống ngày nhưng phần giờ hỏng → giữ hành vi cũ thay vì ra "Invalid Date".
  if (Number.isNaN(parsed.getTime())) return `${m[3]}/${m[2]}/${m[1]}`;
  return EXPORT_DATE_FORMATTER.format(parsed);
};
